/**
 * Phone utility functions for Brazilian phone number formatting
 * Feature: 004-restore-missing-features
 */

/**
 * Apply Brazilian phone mask to input value
 * Format: (XX) XXXXX-XXXX for mobile, (XX) XXXX-XXXX for landline
 * @param value - Raw or partially formatted phone string
 * @returns Object with formatted display value and raw digits
 */
export function applyPhoneMask(value: string): { formatted: string; raw: string } {
  const digits = value.replace(/\D/g, '')
  let formatted = ''

  if (digits.length === 0) {
    formatted = ''
  } else if (digits.length <= 2) {
    formatted = `(${digits}`
  } else if (digits.length <= 7) {
    formatted = `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  } else if (digits.length <= 10) {
    // Landline: (XX) XXXX-XXXX
    formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6, 10)}`
  } else {
    // Mobile: (XX) XXXXX-XXXX
    formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`
  }

  return { formatted, raw: digits }
}

/**
 * Remove all non-digit characters from phone number
 * @param phone - Formatted or raw phone string
 * @returns String containing only digits
 */
export function cleanPhoneNumber(phone: string): string {
  return phone.replace(/\D/g, '')
}

/**
 * Format phone number with international prefix for WhatsApp
 * @param phone - Raw digits or formatted phone
 * @param countryCode - Optional country code (defaults to "55" for backward compat)
 * @returns Phone with +{countryCode} prefix and no formatting
 */
export function formatPhoneForWhatsApp(phone: string, countryCode?: string): string {
  const digits = cleanPhoneNumber(phone)

  if (countryCode) {
    return `+${digits.startsWith(countryCode) ? digits : countryCode + digits}`
  }

  // Legacy behavior: assume Brazilian
  if (digits.startsWith('55') && digits.length >= 12) {
    return `+${digits}`
  }

  return `+55${digits}`
}

/**
 * Build full WhatsApp number from country code and local digits
 * @param countryCode - DDI digits (e.g., "55", "1", "351")
 * @param localDigits - Clean local phone digits (no DDI, no mask)
 * @returns Full number with DDI prefix, digits only (e.g., "5583987718937")
 */
export function buildWhatsappNumber(countryCode: string, localDigits: string): string {
  const cleaned = cleanPhoneNumber(localDigits)
  return `${countryCode}${cleaned}`
}

/**
 * Extract local number by stripping the DDI prefix
 * @param whatsappNumber - Full number with DDI (e.g., "5583987718937")
 * @param countryCode - DDI to strip (e.g., "55")
 * @returns Local digits without DDI prefix
 */
export function extractLocalNumber(whatsappNumber: string, countryCode: string): string {
  const cleaned = cleanPhoneNumber(whatsappNumber)
  if (cleaned.startsWith(countryCode)) {
    return cleaned.slice(countryCode.length)
  }
  return cleaned
}

/**
 * Validate if phone has minimum required digits
 * Brazilian phones: 10 digits (landline) or 11 digits (mobile)
 * @param phone - Phone string to validate
 * @returns Boolean indicating if phone has valid length
 */
export function isValidPhoneLength(phone: string): boolean {
  const digits = cleanPhoneNumber(phone)
  return digits.length >= 10 && digits.length <= 11
}
